import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Phone, Menu, X, Clock, MapPin, BadgeCheck } from "lucide-react";
import { COMPANY, LOGO_URL } from "../lib/constants";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Nos Services" },
  { to: "/urgence", label: "Dépannage / Urgence" },
  { to: "/professionnels", label: "Professionnels" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  const navLinkCls = ({ isActive }) =>
    `relative py-2 text-sm font-semibold tracking-wide transition-colors ${
      isActive
        ? "text-teal-700"
        : "text-slate-700 hover:text-teal-700"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200"
    >
      {/* Top urgency bar */}
      <div
        data-testid="top-urgency-bar"
        className="bg-slate-900 text-white text-xs md:text-sm"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <a
            data-testid="top-bar-phone"
            href={`tel:${COMPANY.phoneDigits}`}
            className="inline-flex items-center gap-2 font-display font-extrabold tracking-tight text-white hover:text-teal-300 transition-colors"
          >
            <Phone className="w-3.5 h-3.5 text-white" />
            {COMPANY.phone}
          </a>
          <div className="flex items-center gap-4 md:gap-6 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-slate-200">
              <MapPin className="w-3.5 h-3.5 text-teal-300" />
              <span className="font-semibold">Équipe locale</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-200">
              <Clock className="w-3.5 h-3.5 text-teal-300" />
              <span className="font-semibold">24h/24 — 7j/7</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-200">
              <BadgeCheck className="w-3.5 h-3.5 text-teal-300" />
              <span className="font-semibold">Devis gratuit</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center group"
        >
          <img
            src={LOGO_URL}
            alt="Mon Serrurier Breton"
            className="h-16 md:h-20 w-auto object-contain transition-transform group-hover:scale-105"
            data-testid="header-logo"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={navLinkCls}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-emergency-call-btn"
            href={`tel:${COMPANY.phoneDigits}`}
            className="hidden sm:inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-4 md:px-5 py-2.5 shadow-lg shadow-orange-600/20 transition-all hover:shadow-xl pulse-emergency"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden md:inline">{COMPANY.phone}</span>
            <span className="md:hidden">Appeler</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg font-semibold ${
                    isActive
                      ? "bg-teal-50 text-teal-700"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <a
              data-testid="mobile-emergency-call"
              href={`tel:${COMPANY.phoneDigits}`}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-orange-600 text-white font-bold rounded-full px-5 py-3"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
